/* ─────────────────────────────────────────────────────────────────────
   Ziva Beauty — Skin Discovery Quiz
   ziva-quiz.js — built from Ziva_Quiz_Complete_With_OnePagers.pdf
──────────────────────────────────────────────────────────────────────── */

const DISCOUNT_CODE = window.SKINQUIZ_CONFIG?.discountCode || 'SKINQUIZ15';

const PRIORITY_COUNTRIES = ['Singapore', 'Malaysia', 'India', 'United Kingdom', 'United States'];
const OTHER_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda',
  'Argentina','Armenia','Australia','Austria','Azerbaijan',
  'Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium',
  'Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina',
  'Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi',
  'Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic',
  'Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica',
  'Croatia','Cuba','Cyprus','Czechia',
  'Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea',
  'Estonia','Eswatini','Ethiopia',
  'Fiji','Finland','France',
  'Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada',
  'Guatemala','Guinea','Guinea-Bissau','Guyana',
  'Haiti','Honduras','Hungary',
  'Iceland','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan',
  'Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya',
  'Liechtenstein','Lithuania','Luxembourg',
  'Madagascar','Malawi','Maldives','Mali','Malta','Marshall Islands',
  'Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco',
  'Mongolia','Montenegro','Morocco','Mozambique','Myanmar',
  'Namibia','Nauru','Nepal','Netherlands','New Zealand',
  'Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway',
  'Oman',
  'Pakistan','Palau','Palestine','Panama','Papua New Guinea',
  'Paraguay','Peru','Philippines','Poland','Portugal',
  'Qatar',
  'Romania','Russia','Rwanda',
  'Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines',
  'Samoa','San Marino','Sao Tome and Principe','Saudi Arabia',
  'Senegal','Serbia','Seychelles','Sierra Leone',
  'Slovakia','Slovenia','Solomon Islands','Somalia',
  'South Africa','South Korea','South Sudan','Spain','Sri Lanka',
  'Sudan','Suriname','Sweden','Switzerland','Syria',
  'Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste',
  'Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey',
  'Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','Uruguay','Uzbekistan',
  'Vanuatu','Vatican City','Venezuela','Vietnam',
  'Yemen',
  'Zambia','Zimbabwe'
];


/* ── 1. STATE ──────────────────────────────────────────────────────── */
const S = {
  step:    0,
  ans:     {},
  email:   '',
  name:    '',
  country: '',
  hist:    [],
  fwd:     []
};

/* ── 2. ANSWER CONSTANTS ───────────────────────────────────────────── */
const Q = {
  q1: {
    OILY_ALL:    'Noticeably oily across the entire face',
    OILY_TZONE:  'Oily primarily in the T-zone (forehead, nose, chin)',
    TIGHT_DRY:   'Feels tight, dry, or uncomfortable',
    COMFORTABLE: 'Remains balanced and comfortable',
    REACTIVE:    'Becomes reactive (redness, sensitivity, irritation)'
  },
  q2: {
    BREAKOUTS:       'Recurring breakouts',
    BREAKOUTS_MARKS: 'Breakouts that leave marks behind',
    DARK_MARKS:      'Persistent pigmentation or uneven tone',
    LESS_FIRM:       'Loss of firmness or early ageing changes',
    EVERYTHING_IRR:  'Skin reacts easily to products or environment',
    MINIMAL:         'No major concerns, maintaining balance'
  },
  q3: {
    JAW_CHIN:   'Jaw and chin',
    FOREHEAD:   'Forehead',
    CHEEKS:     'Cheeks',
    EVERYWHERE: 'Across multiple areas'
  },
  q4: {
    YES:      'Yes, predictable',
    SOMEWHAT: 'Somewhat, but not always consistent',
    NO:       'No clear pattern'
  },
  q5: {
    UNDER25:      'Under 25',
    BETWEEN25_34: '25–34',
    BETWEEN35_44: '35–44',
    OVER45:       '45 and over'
  },
  q6: {
    OUTDOORS:  'Outdoors (sun, heat, pollution exposure)',
    INDOORS:   'Indoors with air conditioning most of the day',
    MIXED:     'Balanced between indoor and outdoor',
    SPF_DAILY: 'Wear SPF PA 50+++ daily'
  },
  q7: {
    STRESS:    'High stress levels',
    SLEEP:     'Irregular or poor-quality sleep',
    DIET:      'High sugar or refined carbohydrate intake',
    HORMONAL:  'Hormonal fluctuations (cycle, PCOS, etc.)',
    NONE:      'None of the above'
  },
  q8: {
    MINIMAL:  'Minimal (cleanser, moisturiser, SPF)',
    BASIC:    'Minimal + targeted additions (serums or treatments)',
    MULTI:    'Multi-step routine with actives',
    ADVANCED: 'Advanced routine (retinoids, exfoliants, layering multiple actives)'
  },
  q9: {
    UNDER3M:  'Less than 3 months',
    M3_12:    '3–12 months',
    Y1_3:     '1–3 years',
    OVER3Y:   'More than 3 years'
  }
};

/* ── 3. PROFILES ───────────────────────────────────────────────────── */
const PR = {
  profileA: {
    name: 'Breakouts and Dark Marks',
    tags: ['Oily/Combo', 'Breakouts', 'PIH'],
    what: 'Melanin-rich skin has denser, more reactive melanocytes. When a breakout inflames the skin, those melanocytes overproduce pigment as part of the healing response. The lesion clears in days — the dark mark stays visible for months. Every unprotected day of UV exposure restimulates those melanocytes, extending the fade timeline significantly. Post-inflammatory hyperpigmentation (PIH) is not a surface stain — it is melanin deposited in the dermal and epidermal layers, deeper than exfoliation can reach.',
    science: 'PIH persists significantly longer on Fitzpatrick III–V skin due to higher melanocyte density. TXA+niacinamide produced mMASI reductions comparable to hydroquinone at 84 days (Scientific Reports 2025). Ceramide moisturiser use during acne treatment significantly reduced inflammatory lesion counts (Draelos et al., J Drugs Dermatol 2023).',
    routine: [
      { step: '1. Cleanse — morning and evening', text: 'Removes sebum and SPF without raising skin pH above 5.5. Everything that follows depends on this.' },
      { step: '2. Niacinamide 5% — every evening', text: 'Blocks melanosome transfer (pigment moving to surface) and regulates oil production simultaneously.' },
      { step: '3. Tranexamic acid 2% — alternating mornings', text: 'Inhibits plasminogen-prostaglandin pathway, a melanin mechanism separate from niacinamide.' },
      { step: '4. Barrier moisturiser with ceramides — AM and PM', text: 'Ceramides 3:1:1 ratio rebuild what cleansing disturbs. Oily skin still needs this.' },
      { step: '5. Tinted SPF 50+ with iron oxides — every morning', text: 'Iron oxides block visible light which triggers pigmentation independently of UV.' }
    ],
    ziva: [
      { product: 'PorePerfect Rose Cleanser — morning and evening', text: 'Natural surfactants at pH 5 (protects acid mantle), willow bark extract (gentle follicle-clearing), rose hydrosol (anti-inflammatory), aloe (barrier support). Clears pores without the barrier disruption that amplifies PIH on melanin-rich skin.' },
      { product: 'Rose Peptide Moisturiser — evening', text: 'Bisabolol (NF-kB inhibitor — blocks inflammation-to-pigment pathway), ectoin (pollution shield), acetyl hexapeptide-8 + hexapeptide-11 (collagen signalling), grape seed oil (linoleic acid corrects sebum deficiency).' }
    ],
    avoid: [
      'Retinol — inflammatory mechanism produces more dark marks than it resolves on melanin-rich skin.',
      'Squeezing — causes the deepest, longest-lasting marks. Ice the lesion instead.'
    ]
  },

  profileB: {
    name: 'Breakouts, Heals Cleanly',
    tags: ['Oily/Combo', 'Breakouts', 'No Marks'],
    what: 'Sebaceous glands are overproducing — amplified by humidity — and follicles block before a full inflammatory cycle. Pigment cells are less reactive, so marks are minimal. The cycle to break: stripping the skin triggers more sebum, which feeds more breakouts. Most people with this profile are over-cleansing and under-hydrating, creating the exact conditions that perpetuate the problem.',
    science: 'Niacinamide directly inhibits the enzyme DGAT2 in sebaceous glands, reducing sebum production measurably at 8–12 weeks (Cosmetic Dermatology 2024). Postbiotics reduce C. acnes imbalance without disrupting the full microbiome — more targeted than antimicrobial approaches (Int J Mol Sci 2025).',
    routine: [
      { step: '1. Cleanse — morning and evening', text: 'pH-correct, gentle. Skin should feel neutral after washing — never tight.' },
      { step: '2. White willow bark exfoliant — three evenings per week', text: 'Dissolves the sebum plug from within. Not daily — daily exfoliation triggers rebound oiliness.' },
      { step: '3. Niacinamide 5% — every evening on oily zones', text: 'Reduces oil production at the gland level. Consistent use over 8–12 weeks produces measurable results.' },
      { step: '4. Oil-free barrier moisturiser — daily', text: 'Dehydrated skin compensates with more oil. Ceramides and polyglutamic acid maintain hydration without heaviness.' },
      { step: '5. Mineral SPF — every morning', text: 'Zinc oxide provides mild sebum regulation alongside UV protection.' }
    ],
    ziva: [
      { product: 'PorePerfect Rose Cleanser — morning and evening', text: 'Natural surfactants at pH 5, willow bark extract (gentle daily follicle-clearing), rose hydrosol (anti-inflammatory). Does not trigger rebound oiliness — the most common mistake for this profile.' },
      { product: 'Rose Peptide Moisturiser — from month 2 (35+ or early ageing concerns)', text: 'Grape seed oil (linoleic acid corrects sebum composition without adding heaviness), dual peptides (collagen support without retinol breakout risk). Not essential under 35 unless early ageing concerns present.' }
    ],
    stars: [
      '★ If one thing: Stop stripping the barrier. The oil will regulate itself when the barrier is intact. Tight skin after cleansing means the pH is too high.',
      '★ Dehydrated skin is not oily skin. Sebum is not water. You can be oily and dehydrated simultaneously — and most people with this profile are.'
    ],
    avoid: [
      'Daily acid exfoliation — strips the barrier and amplifies oiliness.',
      'Alcohol-based toners — surface drying triggers deeper compensatory oil production.',
      'Skipping moisturiser on oily days — the single most common mistake for this profile.'
    ]
  },

  profileC: {
    name: 'Sensitised — Barrier Reset',
    tags: ['Sensitised', 'Barrier', 'Reactive'],
    what: 'The stratum corneum has been compromised. The lipid matrix is depleted. Moisture escapes. Irritants enter. Products that used to be fine now cause stinging, redness, or unpredictable reactions. This is temporary barrier disruption — caused by over-exfoliation, alkaline cleansers, fragrance, or cumulative active overload. The barrier can repair itself — but it needs the right conditions. The goal is not to add more products. It is to remove what is causing damage.',
    science: 'Ceramide:cholesterol:fatty acid 3:1:1 ratio recommended by ETFAD and Korean Dermatological Association for optimal barrier restoration (Cureus 2026). Centella asiatica showed zero irritation index in 15-day human patch study (Int J Mol Sci 2025). Fragrance (synthetic and natural) is the leading cause of contact dermatitis on disrupted barriers.',
    routine: [
      { step: 'Priority 1 — Weeks 1–2: strip back', text: 'Fragrance-free cleanser (pH 5–5.5) + ceramide moisturiser only. Nothing else. Every addition delays identifying what helps.' },
      { step: 'Priority 2 — Layering order matters', text: 'Apply ceramide moisturiser to damp skin within 60 seconds of cleansing. Water evaporates fast — seal it in.' },
      { step: 'Priority 3 — Add mineral SPF in week 3', text: 'Zinc oxide only. No chemical filters. No fragrance. SPF is the third step, not the second.' },
      { step: 'Priority 4 — Patch test niacinamide 5% in week 4', text: 'Inner arm for three days first. One new product per three-week window from this point.' },
      { step: 'Priority 5 — Stop immediately', text: 'All retinoids, all exfoliating acids, all fragrance (synthetic and natural essential oils), all alcohol-based toners. If it burns, it is damaging.' }
    ],
    keys: [
      'Ceramides NP + AP + cholesterol (3:1:1) — the mortar holding skin cells together',
      'Centella asiatica (CICA) — barrier repair with zero irritation risk',
      'Panthenol (provitamin B5) — accelerates epithelial repair',
      'Glycerin or hyaluronic acid on damp skin — draw water into the stratum corneum',
      'Plant oils rich in linoleic acid — hemp, rosehip, borage lock moisture without congestion',
      'Squalane or thin occlusive at night — prevents overnight transepidermal water loss'
    ],
    ziva: null,
    stars: [
      '★ If one thing: Remove everything for 2 weeks except cleanser + ceramide moisturiser. The barrier needs stillness before it can rebuild.',
      '★ Fragrance is not just synthetic perfume — essential oils are also fragrance and are contact sensitisers on a disrupted barrier.',
      '★ Weeks 1–2 are the hardest. Your skin may look worse before it looks better. This is the inflammation calming down, not new damage.'
    ],
    avoid: [
      'All retinoids, AHAs, BHAs, vitamin C during reset.',
      'Fragrance in any form — including natural essential oils.',
      'Adding anything new in weeks 1 and 2.',
      'Hot water, physical scrubs, or over-cleansing — mechanical disruption delays recovery.'
    ]
  },

  profileD: {
    name: 'Hormonal — Jaw and Chin',
    tags: ['Hormonal', 'Jawline', 'Cycle-Aware'],
    what: 'Jaw and chin breakouts that follow a monthly cycle are driven by androgens spiking before menstruation. Sebaceous glands in this zone are more sensitive to this hormonal signal. The trigger is predictable — the topical approach manages the surface. What you can control: the skin environment and the inflammatory response when the hormonal trigger occurs.',
    science: 'Androgen-sensitive sebaceous glands at the jaw and chin are well-established in hormonal acne literature (J Am Acad Dermatol 2021). Azelaic acid 10% inhibits 5-alpha-reductase (the enzyme converting testosterone to DHT at the gland level) and tyrosinase simultaneously. Centella asiatica reduces pre-period inflammatory intensity before lesions form (Cosmetics 2025).',
    routine: [
      { step: 'Every day — the foundation', text: 'Gentle cleanser + niacinamide 5% + barrier moisturiser + tinted SPF. Consistent throughout the month.' },
      { step: 'Days 21–28 — pre-period window', text: 'Increase exfoliation on jaw and chin only. Add centella asiatica mist after cleansing — reduces inflammatory intensity before lesions form.' },
      { step: 'Days 1–7 — active period', text: 'Ice compress on cystic lesions. Skip exfoliation. Continue niacinamide. Do not squeeze.' },
      { step: 'Days 7–21 — intervention window', text: 'Most effective window for actives. Tranexamic acid or azelaic acid on jaw and chin. Tinted SPF every morning.' }
    ],
    ziva: [
      { product: 'PorePerfect Rose Cleanser — morning and evening', text: 'Rose hydrosol reduces pre-period inflammation at the cleansing step. Willow bark prevents follicle blockage before the hormonal surge amplifies it.' },
      { product: 'Rose Peptide Moisturiser — every evening', text: 'Bisabolol (NF-kB inhibitor reduces the inflammatory response to the hormonal trigger), ectoin (environmental protection), dual peptides (collagen support for 35+ without triggering breakouts).' }
    ],
    stars: [
      '★ If one thing: Track your cycle and adjust actives around it. The hormonal trigger is predictable — your routine should match that rhythm.',
      '★ Jaw and chin cystic breakouts — ice the lesion, do not squeeze. Squeezing causes the deepest, longest-lasting marks on this zone.'
    ],
    avoid: [
      'New actives in days 21–28 — skin is most reactive in the pre-period window.',
      'Squeezing cystic jaw lesions — causes deep damage and the longest-lasting marks.',
      'High-glycaemic foods in the pre-period window — amplify the androgen sebum surge.'
    ]
  },

  profileE: {
    name: 'Pigmentation and Firming After 35',
    tags: ['Ageing', 'Pigmentation', 'Peptides'],
    what: 'After 35, collagen declines approximately 1% per year and the cell cycle slows to 45–60 days. Melanocytes have accumulated UV damage and become more reactive. On melanin-rich skin this shows as deepening pigmentation and loss of firmness. Every active here is chosen to support collagen and reduce pigmentation without triggering inflammation — the shared mechanism that would worsen both.',
    science: 'Bakuchiol equivalent to retinol for wrinkle and pigmentation at 12 weeks with significantly less irritation (Dhaliwal et al., BJD 2019). Signal peptides improve collagen synthesis and decrease inflammation simultaneously (PMC11762834, 2025). TXA+niacinamide+vitamin C combination addresses hyperpigmentation via three independent mechanisms (MDPI Cosmetics 2024).',
    routine: [
      { step: 'Morning 1 — Vitamin C (stable form: SAP or ascorbyl glucoside)', text: 'Inhibits tyrosinase and protects collagen from UV degradation. L-ascorbic acid oxidises in tropical heat — use stable derivatives.' },
      { step: 'Morning 2 — Bakuchiol 0.5%', text: 'Triggers the same collagen gene expression as retinol via a non-inflammatory pathway. No PIH risk. Photostable.' },
      { step: 'Morning 3 — Ceramide moisturiser', text: 'Barrier efficiency declines after 35. Ceramides improve the absorption of every active applied above.' },
      { step: 'Morning 4 — Tinted SPF 50+ with iron oxides', text: 'Iron oxides block visible light which triggers melanin via opsin-3 receptors independently of UV — even indoors.' },
      { step: 'Evening 1 — Gentle cleanser', text: '' },
      { step: 'Evening 2 — Tranexamic acid 2%', text: 'Attacks pigmentation via the prostaglandin pathway — separate from vitamin C and niacinamide.' },
      { step: 'Evening 3 — Niacinamide 5–10% on pigmented zones', text: 'Blocks melanosome transfer — the third independent angle on pigmentation.' },
      { step: 'Evening 4 — Bakuchiol + richer ceramide moisturiser', text: 'Collagen support overnight while the skin repair cycle peaks.' }
    ],
    ziva: [
      { product: 'Rose Peptide Moisturiser — PM routine', text: 'Bisabolol (anti-inflammatory preventing PIH formation), ectoin (environmental protection), acetyl hexapeptide-8 + hexapeptide-11 (dual peptide collagen support), grape seed oil (barrier-supportive without congestion).' },
      { product: 'PorePerfect Rose Cleanser — AM and PM', text: 'pH-calibrated foundation. Gentle enough for ageing skin, effective enough to remove SPF completely.' }
    ],
    stars: [
      '★ If one thing: Bakuchiol over retinol. Same collagen results, zero PIH risk, no purging period.',
      '★ Tinted iron oxide SPF is the most important anti-ageing step for melanin-rich skin. Visible light triggers melanin independently of UV.'
    ],
    avoid: [
      'Retinol as first-choice anti-ageing — its inflammation mechanism creates PIH on this skin type.',
      'L-ascorbic acid in clear packaging — oxidises within weeks in tropical storage.',
      'IPL and fractional laser without dermatologist consultation — high PIH risk for Fitzpatrick III–V.'
    ]
  },

  profileF: {
    name: 'Breakouts, Dark Marks, and Firming',
    tags: ['Breakouts', 'PIH', 'Ageing'],
    what: 'The most common presentation for melanin-rich skin after 35. Inflammation is the shared driver of all three. Every breakout accelerates collagen loss in the surrounding tissue. Every inflammatory event deposits pigment. The skin is simultaneously breaking out, marking, and ageing — not because you are doing something wrong, but because inflammation connects all three biologically.',
    science: 'Inflammaging research confirms chronic low-grade inflammation drives collagen degradation, elastin loss, and barrier thinning simultaneously (J Dermatol Sci 2023). TXA+kojic acid+niacinamide: 81% PIH reduction and 60% melasma improvement at 12 weeks in Fitzpatrick III–V skin (JDD 2023). Ceramide moisturiser alongside acne treatment: significantly lower inflammatory lesion counts and faster barrier recovery (Draelos et al., J Drugs Dermatol 2023).',
    routine: [
      { step: 'Morning 1 — Gentle cleanser', text: 'pH-correct. Fragrance-free. Sets the environment every other step depends on.' },
      { step: 'Morning 2 — Vitamin C SAP 5–10%', text: 'Inhibits tyrosinase (marks) and protects collagen (firming) — two of three concerns in one step.' },
      { step: 'Morning 3 — Niacinamide 5%', text: 'Regulates sebum (breakouts) and blocks melanin transfer (marks).' },
      { step: 'Morning 4 — Ceramide moisturiser with bakuchiol 0.5%', text: 'Barrier repair and collagen support — zero PIH risk.' },
      { step: 'Morning 5 — Tinted SPF 50+ with iron oxides', text: 'Without this, actives address pigmentation while UV creates more of it simultaneously.' },
      { step: 'Evening 1 — Gentle cleanser', text: '' },
      { step: 'Evening 2 — Tranexamic acid 2% (three evenings per week)', text: 'Third angle on pigmentation. 81% PIH reduction at 12 weeks with TXA+kojic acid+niacinamide (JDD 2023).' },
      { step: 'Evening 3 — White willow bark (alternating evenings)', text: 'Follicle clearing on alternate nights. Alternating prevents over-loading the barrier.' },
      { step: 'Evening 4 — Bakuchiol 0.5% + ceramide moisturiser with peptides', text: 'Collagen support overnight.' }
    ],
    ziva: [
      { product: 'PorePerfect Rose Cleanser — AM and PM', text: 'pH 5, willow bark (gentle daily follicle-clearing), rose hydrosol (anti-inflammatory foundation). Clears pores without the barrier disruption that worsens all three concerns.' },
      { product: 'Rose Peptide Moisturiser — PM', text: 'Bisabolol (NF-kB inhibitor — addresses the inflammation driving all three concerns), ectoin (pollution shield), acetyl hexapeptide-8 + hexapeptide-11 (collagen support), grape seed oil (sebum correction).' }
    ],
    stars: [
      '★ If one thing: Address inflammation first, everything else follows. Bisabolol, centella, calendula reduce the trigger driving all three concerns.',
      '★ This is not three separate problems requiring three separate routines. It is one problem — inflammation — with three visible outcomes.'
    ],
    avoid: [
      'Retinol — its inflammation mechanism worsens all three concerns simultaneously. Bakuchiol is the correct choice.',
      'More than one new product per three-week window — with three concerns you must identify what is improving.',
      'Glycolic acid as the primary exfoliant — higher PIH risk on melanin-rich skin. Mandelic or lactic acid are gentler alternatives.'
    ]
  },

  profileG: {
    name: 'Extremely Dry or Dehydrated',
    tags: ['Dry', 'Dehydrated', 'Barrier'],
    what: 'The lipid matrix in your stratum corneum is depleted — ceramide levels are measurably low, transepidermal water loss (TEWL) is high, and the skin cannot hold moisture regardless of how much product is applied. This is genuine oil deficiency — sebaceous glands are underproducing. Without adequate lipids, the barrier cannot seal. Humectants without occlusives make the tightness worse. The skin needs both water and the lipids to trap it, in that order, applied correctly.',
    science: 'Ceramide-dominant formulations significantly improve TEWL and stratum corneum hydration within 2–4 weeks. Occlusives applied over humectants reduce moisture loss by up to 98% compared to humectants alone (Cureus 2026). Polyglutamic acid holds 5000× its weight in water and inhibits hyaluronidase, preserving the skin\'s own HA.',
    routine: [
      { step: 'Priority 1 — Layer correctly', text: 'Humectants (glycerin, hyaluronic acid) → oils (rosehip, hemp, avocado) → balm (shea, squalane). Each layer locks in the one before it.' },
      { step: 'Priority 2 — Ceramide-dominant barrier repair', text: 'Look for 3+ ceramides in the first 10 ingredients. Without them, water escapes no matter how much you apply.' },
      { step: 'Priority 3 — Humectants on damp skin', text: 'Polyglutamic acid or hyaluronic acid immediately after cleansing. Apply it fast before evaporation.' },
      { step: 'Priority 4 — Plant oils rich in essential fatty acids', text: 'Avocado, borage, hemp seed, rosehip, sunflower — rebuild the lipid barrier with omega-3, omega-6, omega-9.' },
      { step: 'Priority 5 — Occlusive seal to lock it in', text: 'Shea butter, illipe butter, or squalane as the final layer. Without this seal, everything beneath evaporates.' }
    ],
    keys: [
      'Oil-based or micellar cleanser — foaming strips the minimal oil the skin is producing',
      'Ceramides + cholesterol + fatty acids 3:1:1 — essential for lipid deficiency',
      'Polyglutamic acid or sodium hyaluronate — humectants that hold water',
      'Plant oils high in linoleic acid — rosehip, hemp seed, borage repair the lipid matrix',
      'Occlusive balms or butters — shea, cocoa, illipe, squalane seal everything in',
      'W/O emulsions (water-in-oil) — promote barrier regeneration more than O/W formulas'
    ],
    ziva: null,
    stars: [
      '★ If one thing: Layer correctly — water-based → oil → occlusive balm. Skipping the occlusive means everything beneath evaporates overnight.',
      '★ Dehydrated skin needs water (humectants). Dry skin needs oil (lipids). Extremely dry skin needs both, layered correctly.',
      '★ Cleanse only at night. Water rinse in the morning. Over-cleansing strips the minimal lipids your skin is producing.'
    ],
    avoid: [
      'Foaming cleansers — strip the minimal oil the skin is producing.',
      'Skipping the occlusive step at night — humectants evaporate without a final seal.',
      'Alcohol, fragrance, high-percentage niacinamide — all increase TEWL on already compromised dry skin.',
      'Over-exfoliation — dry skin needs barrier support first, not more turnover.'
    ]
  },

  profileH: {
    name: 'Combination — Dry Cheeks, Oily T-Zone',
    tags: ['Combination', 'Zone-Specific', 'T-Zone'],
    what: 'Two different sebaceous gland densities across the face create two different skin environments. The T-zone has more active sebaceous glands producing oil. The cheeks, temples, and jawline have fewer glands and behave more like dry skin. Most products over-treat one zone while under-treating the other. This requires zone-specific application, not a single product applied uniformly everywhere.',
    science: 'Sebaceous gland density varies significantly across facial zones — T-zone activity is measurably higher than cheek zones (J Invest Dermatol 2020). Niacinamide regulates sebum without drying, making it appropriate for combination profiles when applied selectively.',
    routine: [
      { step: '1. Cleanse — AM and PM, full face', text: 'Gentle foaming or gel cleanser, pH 5–5.5. The one step that can be uniform.' },
      { step: '2. Lightweight hydration — full face', text: 'Polyglutamic acid or hyaluronic acid. Both zones need water — the difference is in sealing.' },
      { step: '3. Niacinamide 5% — T-zone only, evenings', text: 'Regulates sebum where it overproduces. Skip on dry cheeks unless they have pigmentation concerns.' },
      { step: '4. Moisturiser — zone-specific application', text: 'Lightweight gel or lotion on T-zone. Richer emollient cream on cheeks and jawline. Two textures, one routine.' },
      { step: '5. SPF — full face, lightweight formula', text: 'Mineral or hybrid SPF that does not feel heavy.' }
    ],
    keys: [
      'Niacinamide 5–10% — regulates sebum in the T-zone without drying',
      'Polyglutamic acid — lightweight humectant for the entire face, does not congest',
      'Squalane — non-comedogenic occlusive for dry cheeks (skip on T-zone)',
      'Green tea extract — sebum-regulating and anti-inflammatory for T-zone',
      'Linoleic-rich oils for dry zones — rosehip, hemp seed, grape seed',
      'Gel-cream hybrid textures — work across both zones without being too heavy or too light'
    ],
    ziva: null,
    stars: [
      '★ If one thing: Zone-specific application — T-zone and cheeks need different moisturiser textures, not different products.',
      '★ Combination skin is not inconsistent skin — it is normal sebaceous gland distribution. Work with it, not against it.',
      '★ You can use the same cleanser and hydration step for the entire face. The difference is in the moisturiser and actives.'
    ],
    avoid: [
      'Applying the same moisturiser thickness everywhere — T-zone and cheeks have different needs.',
      'Mattifying products with alcohol or clays on the entire face — over-dries the already dry zones.',
      'Rich occlusives on the T-zone — congestion risk without added benefit.',
      'Skipping moisturiser on oily zones — dehydration triggers compensatory oil production.'
    ]
  }
};

/* ── 4. QUESTIONS ──────────────────────────────────────────────────── */
const QS = {
  welcome: { type: 'welcome' },
  q1: {
    chip: 'Skin behaviour',
    q:    'By mid-day (without touching your face), how does your skin typically behave?',
    sub:  'Observe — don\'t guess. What does your skin actually do by noon, without product on?',
    type: 'single',
    opts: [Q.q1.OILY_ALL, Q.q1.OILY_TZONE, Q.q1.TIGHT_DRY, Q.q1.COMFORTABLE, Q.q1.REACTIVE]
  },
  q2: {
    chip: 'Primary concern',
    q:    'What concerns are most consistent for your skin?',
    sub:  'Select the closest match to your current experience.',
    type: 'single',
    opts: [Q.q2.BREAKOUTS, Q.q2.BREAKOUTS_MARKS, Q.q2.DARK_MARKS, Q.q2.LESS_FIRM, Q.q2.EVERYTHING_IRR, Q.q2.MINIMAL]
  },
  q3: {
    chip: 'Breakout location',
    q:    'Where do breakouts appear?',
    sub:  'Location is a clinical signal — be specific.',
    type: 'single',
    opts: [Q.q3.JAW_CHIN, Q.q3.FOREHEAD, Q.q3.CHEEKS, Q.q3.EVERYWHERE]
  },
  q4: {
    chip: 'Cycle pattern',
    q:    'Do these breakouts follow a predictable pattern?',
    sub:  'This separates hormonal acne from non-hormonal. The protocol differs significantly.',
    type: 'single',
    opts: [Q.q4.YES, Q.q4.SOMEWHAT, Q.q4.NO]
  },
  q5: {
    chip: 'Age range',
    q:    'Your age range',
    sub:  'Skin biology changes significantly by decade.',
    type: 'single',
    opts: [Q.q5.UNDER25, Q.q5.BETWEEN25_34, Q.q5.BETWEEN35_44, Q.q5.OVER45]
  },
  q6: {
    chip: 'Environment',
    q:    'Which environment does your skin spend most time in?',
    sub:  'This calibrates SPF urgency and hydration recommendations.',
    type: 'single',
    opts: [Q.q6.OUTDOORS, Q.q6.INDOORS, Q.q6.MIXED, Q.q6.SPF_DAILY]
  },
  q7: {
    chip: 'Lifestyle',
    q:    'Which of these factors apply to your current lifestyle?',
    sub:  'Select all that apply.',
    type: 'multi',
    opts: [Q.q7.STRESS, Q.q7.SLEEP, Q.q7.DIET, Q.q7.HORMONAL, Q.q7.NONE]
  },
  q8: {
    chip: 'Current routine',
    q:    'Which best describes your current skincare approach?',
    sub:  'Be honest — this calibrates how much we need to add, adjust, or strip back.',
    type: 'single',
    opts: [Q.q8.MINIMAL, Q.q8.BASIC, Q.q8.MULTI, Q.q8.ADVANCED]
  },
  q9: {
    chip: 'Duration',
    q:    'How long have you been dealing with your current skin concerns?',
    sub:  'This sets realistic expectations for your timeline.',
    type: 'single',
    opts: [Q.q9.UNDER3M, Q.q9.M3_12, Q.q9.Y1_3, Q.q9.OVER3Y]
  },
  email: {
    chip: 'Your profile is ready',
    q:    'Your skin profile is ready.',
    sub:  'Enter your email to receive your full analysis — your exact skin behaviour profile, the biological triggers affecting it, and a step-by-step routine tailored to your skin and environment. Please ensure care@zivabeauty.co is not marked as spam.',
    type: 'email'
  }
};

/* ── 5. FLOW LOGIC ─────────────────────────────────────────────────── */
function getFlow() {
  const f  = ['welcome', 'q1', 'q2'];
  const q2 = S.ans.q2;
  if (q2 === Q.q2.BREAKOUTS || q2 === Q.q2.BREAKOUTS_MARKS) f.push('q3');
  const q3 = S.ans.q3;
  if (q3 === Q.q3.JAW_CHIN) f.push('q4');
  f.push('q5', 'q6', 'q7', 'q8', 'q9', 'email', 'result');
  return f;
}
function curQ()   { return getFlow()[S.step] || 'result'; }
function totalQ() { return Math.max(1, getFlow().length - 3); }
function stepNum() {
  const qid = curQ();
  const f = getFlow().filter(id => !['welcome', 'email', 'result'].includes(id));
  const idx = f.indexOf(qid);
  return idx > -1 ? idx + 1 : null;
}
function pct() {
  const qid = curQ();
  if (qid === 'welcome') return 0;
  if (qid === 'result')  return 100;
  const f   = getFlow().filter(id => !['welcome', 'email', 'result'].includes(id));
  const idx = f.indexOf(qid);
  return Math.min(98, Math.round(((idx + 1) / Math.max(1, f.length)) * 90));
}

function evaluateProfile() {
  const q1 = S.ans.q1;
  const q2 = S.ans.q2;
  const q3 = S.ans.q3;
  const q4 = S.ans.q4;
  const q5 = S.ans.q5;
  const q7 = S.ans.q7 || [];
  const q8 = S.ans.q8;

  const age35plus    = q5 === Q.q5.BETWEEN35_44 || q5 === Q.q5.OVER45;
  const hasBreakouts = q2 === Q.q2.BREAKOUTS || q2 === Q.q2.BREAKOUTS_MARKS;
  const cyclical     = q4 === Q.q4.YES || q4 === Q.q4.SOMEWHAT;
  const hormonalLife = q7.includes(Q.q7.HORMONAL);
  const reactive     = q1 === Q.q1.REACTIVE || q2 === Q.q2.EVERYTHING_IRR;

  // Hard override → Profile C (reactive skin OR advanced routine on reactive/irritated skin)
  if (reactive) return 'profileC';
  if (q8 === Q.q8.ADVANCED && reactive) return 'profileC';

  // Profile G — tight and dry, no breakouts
  if (q1 === Q.q1.TIGHT_DRY) return 'profileG';

  // Profile H — oily T-zone + minimal concerns
  if (q1 === Q.q1.OILY_TZONE && q2 === Q.q2.MINIMAL) return 'profileH';

  // Profile D — breakouts + jaw/chin + cyclical pattern + hormonal lifestyle flag
  if (hasBreakouts && q3 === Q.q3.JAW_CHIN && cyclical && hormonalLife) return 'profileD';
  // Profile D — breakouts + jaw/chin + cyclical (without hormonal flag still qualifies)
  if (hasBreakouts && q3 === Q.q3.JAW_CHIN && cyclical) return 'profileD';

  // Profile F — breakouts + marks + 35+
  if (q2 === Q.q2.BREAKOUTS_MARKS && age35plus) return 'profileF';

  // Profile A — breakouts + marks (under 35)
  if (q2 === Q.q2.BREAKOUTS_MARKS) return 'profileA';

  // Profile E — pigmentation / firming + 35+
  if ((q2 === Q.q2.DARK_MARKS || q2 === Q.q2.LESS_FIRM) && age35plus) return 'profileE';
  if (q2 === Q.q2.DARK_MARKS || q2 === Q.q2.LESS_FIRM) return 'profileE';

  // Profile B — breakouts only, heals cleanly
  if (q2 === Q.q2.BREAKOUTS) return 'profileB';

  return 'profileC';
}

/* ── 6. RENDER FUNCTIONS ───────────────────────────────────────────── */
function render() {
  const qid = curQ();
  if (qid === 'welcome') { renderWelcome(); return; }
  if (qid === 'result')  { renderResultLoading(); return; }

  const q = QS[qid];
  if (!q) return;

  if (!el('foot') || !el('fill') || !el('stepcount') || !el('bk') || !el('nxt') || !el('body')) return;
  el('foot').style.display = 'flex';
  const sn = stepNum();
  el('fill').style.width = pct() + '%';
  el('stepcount').textContent = sn ? sn + ' of ' + totalQ() : '';
  el('bk').style.visibility   = S.step > 0 ? 'visible' : 'hidden';
  if (el('fwd')) el('fwd').style.visibility = S.fwd.length > 0 ? 'visible' : 'hidden';

  const nxt = el('nxt');

  if (qid === 'email') {
    nxt.textContent = 'Get my guide + code';
    nxt.style.display = '';
    nxt.disabled = true;
    const detectedCountry = (window.ZIVA_CONFIG && window.ZIVA_CONFIG.country) || '';
    el('body').innerHTML = `
      <div class="chip">${q.chip}</div>
      <div class="qt">${q.q}</div>
      <div class="sub">${q.sub}</div>
      <input class="einp" id="ni" type="text"  placeholder="Your first name"  value="${S.name}">
      <input class="einp" id="ei" type="email" placeholder="Email address"    value="${S.email}">
      ${buildCountrySelect(detectedCountry)}
      <label style="display:flex;align-items:flex-start;gap:10px;margin-top:10px;cursor:pointer">
        <input type="checkbox" id="consent" style="margin-top:3px;flex-shrink:0;width:15px;height:15px;accent-color:var(--primary);cursor:pointer">
        <span style="font-family:'Lato',sans-serif;font-size:12px;color:var(--text);line-height:1.55">Yes! Send my personalised routine + discount code to my inbox, and keep me in the loop on new launches and offers. Unsubscribe anytime.</span>
      </label>`;
    setTimeout(() => {
      const ei      = el('ei');
      const consent = el('consent');
      const validate = () => {
        const emailOk   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ei ? ei.value : '');
        const consentOk = consent ? consent.checked : false;
        nxt.disabled = !(emailOk && consentOk);
      };
      if (ei)      ei.addEventListener('input', validate);
      if (consent) consent.addEventListener('change', validate);
      validate();
    }, 30);
    return;
  }

  const isMulti = q.type === 'multi';
  const sel = S.ans[qid] || (isMulti ? [] : null);

  // Single: hide Continue button (auto-advances on pick)
  // Multi: show Continue button
  nxt.style.display = isMulti ? '' : 'none';
  nxt.textContent   = 'Continue';
  nxt.disabled      = isMulti ? sel.length === 0 : false;

  let html = `<div class="chip">${q.chip}</div><div class="qt">${q.q}</div><div class="sub">${q.sub}</div>`;
  if (isMulti) html += `<p class="hint">Select all that apply</p>`;
  html += `<div class="opts">`;
  q.opts.forEach(o => {
    const on  = isMulti ? (sel || []).includes(o) : sel === o;
    const esc = o.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    if (isMulti) {
      html += `<button class="opt${on ? ' on' : ''}" onclick="pick('${qid}','${esc}',true)">
        <span class="chk">${on ? '<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}</span>
        <span>${o}</span></button>`;
    } else {
      html += `<button class="opt${on ? ' on' : ''}" onclick="pick('${qid}','${esc}',false)">
        <span class="dot"><span class="di"></span></span><span>${o}</span></button>`;
    }
  });
  html += `</div>`;
  el('body').innerHTML = html;
}

function el(id) { return document.getElementById(id); }

function buildCountrySelect(detected) {
  const opt = c => `<option value="${c}"${c === detected ? ' selected' : ''}>${c}</option>`;
  return `<select class="einp" id="ci">
    <option value=""${detected ? '' : ' selected'} disabled>Country of residence</option>
    ${PRIORITY_COUNTRIES.map(opt).join('')}
    <option value="" disabled>──────────────────</option>
    ${OTHER_COUNTRIES.map(opt).join('')}
  </select>`;
}

function renderWelcome() {
  if (!el('fill') || !el('bk') || !el('stepcount') || !el('nxt') || !el('body')) return;
  el('fill').style.width = '0%';
  el('bk').style.visibility = 'hidden';
  el('stepcount').textContent = '';

  const hasStarted = S.fwd.length > 0;
  const nxt = el('nxt');
  const fwd = el('fwd');

  if (hasStarted) {
    nxt.style.display = 'none';
    if (fwd) fwd.style.visibility = 'visible';
  } else {
    nxt.style.display = '';
    nxt.textContent = 'Start your skin analysis';
    nxt.disabled = false;
    if (fwd) fwd.style.visibility = 'hidden';
  }

  el('body').innerHTML = `
    <div style="display:flex;flex-direction:column;justify-content:space-between;height:100%;text-align:center">
      <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:1.5rem 0.5rem">
        <p style="font-family:'Playfair Display',serif;font-size:18px;font-weight:400;color:var(--heading);line-height:1.65;margin-bottom:1.2rem">Your skin is not random.<br>It is responding to patterns you have not been shown yet.</p>
        <p style="font-family:'Lato',sans-serif;font-size:13px;color:var(--text);line-height:1.75;max-width:380px;margin-bottom:0.8rem">In under 5 minutes, this assessment maps what your skin is doing, why it keeps repeating, and what will actually change it.</p>
        <p style="font-family:'Lato',sans-serif;font-size:13px;color:var(--text);line-height:1.75;max-width:380px;margin-bottom:1.2rem">You will receive a personalised report with your skin behaviour profile, the biological triggers behind it, and a routine that works with your skin — not against it.</p>
        <p style="font-family:'Montserrat',sans-serif;font-size:11px;letter-spacing:0.08em;color:var(--muted);text-transform:uppercase">Free&nbsp;&nbsp;·&nbsp;&nbsp;Private&nbsp;&nbsp;·&nbsp;&nbsp;Built on formulation science</p>
      </div>
      <div style="font-family:'Lato',sans-serif;font-size:10px;color:var(--muted);line-height:1.5;padding-top:12px;border-top:1px solid var(--border);margin-top:12px">
        This quiz provides general skincare guidance for informational purposes only. It is not a medical diagnosis and does not substitute for advice from a qualified dermatologist. If you have a skin condition, please consult a healthcare professional.
      </div>
    </div>`;
}

function renderResultLoading() {
  el('foot').style.display = 'none';
  el('fill').style.width = '95%';
  el('stepcount').textContent = '';

  el('body').innerHTML = `
    <div style="text-align:center;padding:3rem 1rem 2rem">
      <div style="margin-bottom:1.2rem"><div class="spinner"></div></div>
      <div style="font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:500;color:var(--heading);margin-bottom:8px">Reading your skin story…</div>
      <div style="font-family:'Lato',sans-serif;font-size:12px;color:var(--highlight);line-height:1.6;max-width:300px;margin:0 auto">Your answers are being matched to the exact profile logic from the Ziva protocol.</div>
    </div>`;

  setTimeout(renderResult, 250);
}

function renderResult() {
  el('foot').style.display = 'none';
  el('fill').style.width = '100%';
  el('stepcount').textContent = '';

  const pid    = evaluateProfile();
  const pr     = PR[pid];
  const shopUrl = (window.ZIVA_CONFIG && window.ZIVA_CONFIG.shopUrl) || 'https://zivabeauty.co/collections/frontpage';

  el('body').innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:2rem 1rem;height:100%">

      <div style="font-family:'Montserrat',sans-serif;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted);margin-bottom:10px">Your skin profile</div>
      <div style="font-family:'Playfair Display',serif;font-size:26px;font-weight:400;color:var(--heading);line-height:1.3;margin-bottom:10px">${pr.name}</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;justify-content:center;margin-bottom:1.6rem">
        ${pr.tags.map(t => `<span class="ptag">${t}</span>`).join('')}
      </div>

      <div style="font-family:'Lato',sans-serif;font-size:13px;color:var(--text);line-height:1.75;max-width:360px;margin-bottom:1.6rem">
        Your personalised skin report and the one time discount code are on their way to <strong>${S.email || 'your inbox'}</strong>. Check your inbox — it should arrive within a few minutes. If you cant find it, check spam and mark care@zivabeauty.co as not spam.
      </div>

      <button class="shop-btn" style="max-width:320px" onclick="window.location.href='${shopUrl}'">Explore what was built for your skin &rarr;</button>

    </div>`;
}

/* ── 7. USER INTERACTION ───────────────────────────────────────────── */
function pick(qid, val, multi) {
  if (multi) {
    if (!S.ans[qid]) S.ans[qid] = [];
    const i = S.ans[qid].indexOf(val);
    if (i > -1) S.ans[qid].splice(i, 1);
    else         S.ans[qid].push(val);
    render();
  } else {
    S.ans[qid] = val;
    S.fwd = [];
    render(); // flash the selection
    setTimeout(next, 280);
  }
}

async function subscribeToKlaviyo(email, firstName, country) {
  try {
    const pid       = evaluateProfile();
    const pr        = PR[pid];
    const companyId = window.KLAVIYO_PUBLIC_KEY;
    const listId    = window.KLAVIYO_LIST_ID;
    const headers   = { 'content-type': 'application/json', 'revision': '2024-10-15' };

    const properties = {
      skin_profile:    pr.name,
      skin_profile_id: pid,
      profile_code:    DISCOUNT_CODE,
      profile_tags:    pr.tags.join(', ')
    };
    if (country) properties.country = country;

    // 1. Create / update profile
    await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${companyId}`, {
      method:  'POST',
      headers,
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: { email, first_name: firstName, properties }
        }
      })
    });

    // 2. Subscribe to email list
    if (listId) {
      const subBody = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId
              }
            }
          }
        }
      };
      await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(subBody)
      });
    }
  } catch (err) {
    console.warn('Klaviyo subscribe failed:', err);
  }
}

async function next() {
  const qid = curQ();
  if (qid === 'email') {
    const ei      = el('ei');
    const ni      = el('ni');
    const ci      = el('ci');
    const consent = el('consent');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ei.value)) return;
    S.email   = ei.value;
    S.name    = ni ? ni.value : '';
    S.country = ci ? ci.value.trim() : '';
    if (consent && consent.checked) {
      await subscribeToKlaviyo(S.email, S.name, S.country);
    }
  }
  S.fwd = [];
  S.hist.push(S.step);
  S.step++;
  const f = getFlow();
  while (S.step < f.length - 1 && !QS[f[S.step]]) S.step++;
  render();
}

function back() {
  if (S.hist.length > 0) {
    S.fwd.push(S.step);
    S.step = S.hist.pop();
    render();
  }
}

function forward() {
  if (S.fwd.length > 0) {
    S.hist.push(S.step);
    S.step = S.fwd.pop();
    render();
  }
}

function copyDiscountCode() {
  navigator.clipboard.writeText(DISCOUNT_CODE).then(() => {
    const confirm = el('copy-confirm');
    if (confirm) {
      confirm.style.display = 'inline';
      setTimeout(() => { confirm.style.display = 'none'; }, 2500);
    }
  });
}

function closeQuiz() {
  const card = el('card');
  if (card) {
    card.style.animation = 'ziva-fadeOut 0.3s ease forwards';
    setTimeout(() => { window.location.href = '/'; }, 300);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  render();

  const quizModal  = el('ziva-quiz-modal');
  const closeBtnEl = el('close-btn');

  function openModal() {
    if (!quizModal) return;
    quizModal.style.visibility = 'visible';
    quizModal.style.pointerEvents = 'auto';
    document.body.style.overflow = 'hidden';
    renderWelcome();
  }

  function closeModal() {
    if (!quizModal) return;
    quizModal.style.visibility = 'hidden';
    quizModal.style.pointerEvents = 'none';
    document.body.style.overflow = '';
  }

  // Trigger from any a[href="#open-quiz"] on the page
  document.querySelectorAll('a[href="#open-quiz"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openModal();
    });
  });

  if (closeBtnEl) closeBtnEl.addEventListener('click', closeModal);


  // Auto-open when page.ziva-quiz.liquid sets this flag
  if (window.ZIVA_AUTO_OPEN) openModal();
});
